import { MdEdit, MdLocationOn } from 'react-icons/md'
import { formatAddressLine } from '@/components/address/address-fields'
import { formatCEP } from '@/lib/cep'
import type { ServiceAddressSnapshot } from '@/lib/api/common/service-address'

interface ServiceAddressCardProps {
    address?: ServiceAddressSnapshot | null
    /** Quando informado, mostra o atalho para "Alterar endereço de atendimento". */
    onChange?: () => void
}

const SOURCE_LABEL: Record<string, string> = {
    PARTNER: 'Endereço do parceiro',
    PROFESSIONAL: 'Endereço do profissional',
    HOSPITAL: 'Endereço do hospital',
    CUSTOM: 'Endereço informado neste atendimento',
}

/**
 * Mostra o LOCAL DE ATENDIMENTO gravado no atendimento.
 *
 * O endereço era decidido no agendamento e sumia: nenhuma tela de detalhe o
 * exibia, então quem atendia o paciente ao telefone não tinha como confirmar
 * para onde ele deveria ir sem abrir o registro no banco. Aqui ele fica visível
 * junto do paciente e do profissional, com a origem (parceiro, profissional,
 * hospital ou digitado) — é o que explica por que aquele endereço está ali.
 */
export default function ServiceAddressCard({
    address,
    onChange,
}: ServiceAddressCardProps) {
    const unconfirmed = address?.addressSource === 'UNCONFIRMED'
    const line = address
        ? formatAddressLine({
              street: address.street ?? undefined,
              number: address.number ?? undefined,
              complement: address.complement ?? undefined,
              neighborhood: address.neighborhood ?? undefined,
              city: address.city ?? undefined,
          })
        : ''
    const hasAddress = !unconfirmed && !!line

    return (
        <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
            <div className="p-2 bg-secondary/10 rounded-lg">
                <MdLocationOn className="text-xl text-secondary" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-xs text-base-content/60">
                        Local de atendimento
                    </p>
                    {onChange && (
                        <button
                            type="button"
                            onClick={onChange}
                            className="btn btn-ghost btn-xs gap-1"
                            title="Alterar endereço de atendimento"
                        >
                            <MdEdit className="text-sm" />
                            Alterar
                        </button>
                    )}
                </div>

                {hasAddress ? (
                    <>
                        <p className="font-semibold break-words">{line}</p>
                        <p className="text-sm text-base-content/60">
                            {[
                                address?.state,
                                address?.zipCode
                                    ? `CEP ${formatCEP(address.zipCode)}`
                                    : '',
                                address?.addressSource
                                    ? SOURCE_LABEL[address.addressSource]
                                    : '',
                            ]
                                .filter(Boolean)
                                .join(' — ')}
                        </p>
                    </>
                ) : (
                    <p className="font-semibold text-base-content/60">
                        {unconfirmed
                            ? 'Ainda não definido — a equipe informará o paciente'
                            : 'Não informado'}
                    </p>
                )}
            </div>
        </div>
    )
}
